import React, { useCallback, useEffect, useRef, useState } from "react";
import Cropper from "react-cropper";
import "cropperjs/dist/cropper.css";
import PopUp from "../containers/PopUp";
import { canExportCropperCanvas } from "../../utils/productImageLoader";

const CROPPER_HEIGHT = 480;

const MyCropperComp = ({
  save,
  cancel,
  src,
  srcPrev,
  setSrcPrev,
  cropped,
  setCropped,
  preloading = false,
}) => {
  const cropperRef = useRef(null);
  const previewRafRef = useRef(null);
  const [previewNode, setPreviewNode] = useState(null);

  const [isDefault, setIsDefault] = useState(false);
  const canExportCanvas = canExportCropperCanvas(src);
  const useNativePreview = !canExportCanvas;
  const cropperReady = canExportCanvas || Boolean(previewNode);

  useEffect(() => {
    setIsDefault(false);
  }, [src]);

  useEffect(
    () => () => {
      if (previewRafRef.current) {
        cancelAnimationFrame(previewRafRef.current);
      }
    },
    [],
  );

  const updateExportPreview = useCallback(() => {
    const cropperInstance = cropperRef.current?.cropper;
    if (!cropperInstance) return;

    if (!canExportCanvas) {
      setCropped(true);
      return;
    }

    const croppedCanvas = cropperInstance.getCroppedCanvas({
      imageSmoothingEnabled: true,
      imageSmoothingQuality: "high",
    });
    if (!croppedCanvas) return;

    try {
      setSrcPrev(croppedCanvas.toDataURL("image/jpeg", 0.92));
      setCropped(true);
    } catch (err) {
      console.error(err);
    }
  }, [canExportCanvas, setCropped, setSrcPrev]);

  const scheduleExportPreview = useCallback(() => {
    if (previewRafRef.current) return;

    previewRafRef.current = requestAnimationFrame(() => {
      previewRafRef.current = null;
      updateExportPreview();
    });
  }, [updateExportPreview]);

  const onCropperReady = useCallback(() => {
    const cropper = cropperRef.current?.cropper;
    if (!cropper) return;

    cropper.resize();
    cropper.reset();
    updateExportPreview();
  }, [updateExportPreview]);

  const onSave = (e) => {
    e?.preventDefault();
    updateExportPreview();
    save(e, { isDefault });
  };

  if (!src) return null;

  return (
    <PopUp width="920px">
      <div className="crop-editor">
        <aside className="crop-preview-side">
          <p className="crop-preview-side__title">תצוגה מקדימה</p>
          <div className="crop-preview-side__frame">
            {useNativePreview ? (
              <div
                key={src}
                ref={setPreviewNode}
                className="crop-preview-side__live"
                aria-label="תצוגה מקדימה"
              />
            ) : srcPrev ? (
              <img
                src={srcPrev}
                alt="תצוגה מקדימה"
                className="crop-preview-side__export"
              />
            ) : (
              <span className="crop-preview-side__empty">—</span>
            )}
          </div>

          <label className="crop-preview-side__default">
            <input
              type="checkbox"
              checked={isDefault}
              onChange={(e) => setIsDefault(e.target.checked)}
            />
            <span>תמונה ראשית</span>
          </label>
        </aside>

        <div className="crop-editor__main for-crop">
          {cropperReady ? (
            <Cropper
              key={src}
              src={src}
              style={{ height: CROPPER_HEIGHT, width: "100%" }}
              className="crop-editor__cropper"
              guides
              checkCrossOrigin={false}
              preview={useNativePreview ? previewNode : false}
              ref={cropperRef}
              crop={scheduleExportPreview}
              ready={onCropperReady}
              cropmove={scheduleExportPreview}
              cropend={scheduleExportPreview}
              zoom={scheduleExportPreview}
              zoomable
              movable
              cropBoxMovable
              cropBoxResizable
              dragMode="crop"
              background
              autoCropArea={0.85}
              highlight
              viewMode={1}
              minCropBoxHeight={40}
              minCropBoxWidth={40}
              responsive
              restore={false}
            />
          ) : null}
        </div>
      </div>

      <ul className="actions">
        <li>
          <button
            onClick={onSave}
            type="button"
            className="button-green"
            disabled={preloading || !canExportCanvas || !cropped || !srcPrev}
          >
            {preloading ? "שומר..." : "שמור"}
          </button>
          <button
            onClick={cancel}
            type="button"
            className="button-cancel"
            disabled={preloading}
          >
            ביטול
          </button>
        </li>
      </ul>
    </PopUp>
  );
};

export default MyCropperComp;
